import { memo, useState, useCallback, useEffect } from "react";
import { Sidenav } from "./components/Sidenav";
import { NotificationBell } from "./components/NotificationBell";
import { TimezoneSelector } from "./components/TimezoneSelector";
import { ChangePasswordScreen } from "./components/ChangePasswordScreen";
import { refreshToken, isAuthError, clearToken } from "@/infrastructure/auth/authService";
import { useRouter } from "@/application/stores/routerStore";
import { useTenantUsageStore } from "@/application/stores/tenantUsageStore";
import { WarehousesPage } from "./warehouses/WarehousesPage";
import { ProductsPage } from "./products/ProductsPage";
import { InventoryPage } from "./inventory/InventoryPage";
import { ReceiptsPage } from "./inventory/receipts/ReceiptsPage";
import { ReceiptDetailPage } from "./inventory/receipts/ReceiptDetailPage";
import { DispatchesPage } from "./inventory/dispatches/DispatchesPage";
import { DispatchDetailPage } from "./inventory/dispatches/DispatchDetailPage";
import { TransfersPage } from "./inventory/transfers/TransfersPage";
import { TransferDetailPage } from "./inventory/transfers/TransferDetailPage";
import { BalancePage } from "./inventory/balance/BalancePage";
import { KardexPage } from "./inventory/kardex/KardexPage";
import { AdjustmentsPage } from "./inventory/adjustments/AdjustmentsPage";
import { AdjustmentDetailPage } from "./inventory/adjustments/AdjustmentDetailPage";
import { DashboardPage } from "./analytics/DashboardPage";
import { ReportsPage } from "./analytics/ReportsPage";
import { TypeUsePage } from "./type-use/TypeUsePage";
import { TypeUnitPage } from "./type-unit/TypeUnitPage";
import { MovementSubtypePage } from "./movement-subtype/MovementSubtypePage";
import { UnitConversionPage } from "./unit-conversion/UnitConversionPage";
import { CategoryPage } from "./category/CategoryPage";
import { UsersPage } from "./admin/UsersPage";
import { PlanUsagePage } from "./plan/PlanUsagePage";
import { useI18n } from "@/i18n";
import { useRole } from "@/hooks/useRole";
import { useSessionStore } from "@/application/stores/sessionStore";
import type { NavKey } from "./components/Sidenav";

const STORAGE_KEY = "nexo-platform-state";

interface PlatformState {
  collapsed: boolean;
  activeKey: NavKey;
}

function loadState(): PlatformState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as PlatformState;
  } catch {}
  return { collapsed: false, activeKey: "warehouses" };
}

function saveState(state: PlatformState) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

export const PlatformPage = memo(function PlatformPage() {
  const { t } = useI18n();
  const { canSeeNav } = useRole();
  const { navigate } = useRouter();
  const subscriptionStatus = useSessionStore(s => s.subscriptionStatus);
  const mustChangePassword = useSessionStore(s => s.mustChangePassword);
  const setFromToken = useSessionStore(s => s.setFromToken);
  const initial = loadState();
  const [collapsed, setCollapsed] = useState(initial.collapsed);
  const [activeKey, setActiveKey] = useState<NavKey>(initial.activeKey);
  const [receiptDetailId, setReceiptDetailId] = useState<string | null>(null);
  const [dispatchDetailId, setDispatchDetailId] = useState<string | null>(null);
  const [transferDetailId, setTransferDetailId] = useState<string | null>(null);
  const [adjustmentDetailId, setAdjustmentDetailId] = useState<string | null>(null);
  const [subscriptionBlocked, setSubscriptionBlocked] = useState(false);

  // Validate subscription on platform load (skip if must change password)
  useEffect(() => {
    if (mustChangePassword) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await refreshToken();
        if (!cancelled) setFromToken(token);
      } catch (err) {
        if (!cancelled && isAuthError(err) && err.message === "SUBSCRIPTION_EXPIRED") {
          setSubscriptionBlocked(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load tenant usage data (plan limits) for admin/supervisor roles
  const fetchTenantUsage = useTenantUsageStore(s => s.fetch);
  useEffect(() => {
    if (canSeeNav("admin.usage")) {
      fetchTenantUsage();
    }
  }, [canSeeNav, fetchTenantUsage]);

  // Redirect to "warehouses" if the stored/current key is not accessible for this role
  useEffect(() => {
    if (!canSeeNav(activeKey)) {
      setActiveKey("warehouses");
    }
  }, [activeKey, canSeeNav]);

  useEffect(() => {
    saveState({ collapsed, activeKey });
  }, [collapsed, activeKey]);

  const toggleSidenav = useCallback(() => setCollapsed(v => !v), []);
  const handleNavigate = useCallback((key: NavKey) => {
    setActiveKey(key);
    setReceiptDetailId(null);
    setDispatchDetailId(null);
    setTransferDetailId(null);
    setAdjustmentDetailId(null);
    if (window.innerWidth <= 768) setCollapsed(true);
  }, []);

  if (subscriptionBlocked) {
    return (
      <div style={{ minHeight: "100vh", background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "32px", gap: "24px" }}>
        <div style={{ background: "#fff", borderRadius: "24px", padding: "48px 40px", width: "100%", maxWidth: "460px", display: "flex", flexDirection: "column", alignItems: "center", gap: "24px", textAlign: "center" }}>
          <div style={{ width: "52px", height: "52px", background: "#EB5139", borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="26" height="26" viewBox="0 0 20 20" fill="none"><path d="M3 7L10 3L17 7V13L10 17L3 13V7Z" stroke="white" strokeWidth="1.8" strokeLinejoin="round" /><path d="M10 3V17M3 7L17 13M17 7L3 13" stroke="white" strokeWidth="1.1" strokeOpacity="0.6" /></svg>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <h1 style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "1.5rem", fontWeight: 700, color: "#111", margin: 0 }}>{t.login.subscriptionTitle}</h1>
            <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "14px", color: "#666", margin: 0, lineHeight: "1.5" }}>{t.login.subscriptionMessage}</p>
          </div>
          <button
            onClick={() => { clearToken(); navigate("login"); }}
            style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "14px", fontWeight: 600, color: "#555", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "10px", padding: "12px 24px", cursor: "pointer" }}
          >
            {t.login.subscriptionBack}
          </button>
        </div>
      </div>
    );
  }

  if (mustChangePassword) {
    return <ChangePasswordScreen />;
  }

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f7f7f7" }}>
      <Sidenav
        collapsed={collapsed}
        activeKey={activeKey}
        onToggle={toggleSidenav}
        onNavigate={handleNavigate}
      />
      <main style={{ flex: 1, overflow: "auto", minWidth: 0, display: "flex", flexDirection: "column" }}>
        {subscriptionStatus === "PAST_DUE" && (
          <div style={{ background: "#fef3c7", borderBottom: "1px solid #fde68a", padding: "10px 28px", display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
            <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "13px", color: "#92400e" }}>{t.login.pastDueBanner}</span>
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", padding: "10px 28px 10px 16px", flexShrink: 0, gap: "12px" }}>
          {collapsed && <button onClick={toggleSidenav} className="mobile-nav-toggle" style={{ display: "none", background: "none", border: "none", cursor: "pointer", color: "#555", padding: "6px", marginRight: "auto" }}><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 12h18M3 6h18M3 18h18" /></svg></button>}
          <TimezoneSelector />
          <NotificationBell />
        </div>
        <div style={{ flex: 1 }}>
          {activeKey === "warehouses" && <WarehousesPage onNavigateTypeUse={() => handleNavigate("settings.typeUse")} onNavigateUsage={() => handleNavigate("admin.usage")} />}
          {activeKey === "products" && <ProductsPage onNavigateUsage={() => handleNavigate("admin.usage")} />}
          {activeKey === "inventory.stock" && <InventoryPage />}
          {activeKey === "inventory.receipts" && !receiptDetailId && <ReceiptsPage onOpenDetail={setReceiptDetailId} />}
          {activeKey === "inventory.receipts" && receiptDetailId && <ReceiptDetailPage receiptId={receiptDetailId} onBack={() => setReceiptDetailId(null)} />}
          {activeKey === "inventory.dispatches" && !dispatchDetailId && <DispatchesPage onOpenDetail={setDispatchDetailId} />}
          {activeKey === "inventory.dispatches" && dispatchDetailId && <DispatchDetailPage dispatchId={dispatchDetailId} onBack={() => setDispatchDetailId(null)} />}
          {activeKey === "inventory.transfers" && !transferDetailId && <TransfersPage onOpenDetail={setTransferDetailId} />}
          {activeKey === "inventory.transfers" && transferDetailId && <TransferDetailPage transferId={transferDetailId} onBack={() => setTransferDetailId(null)} />}
          {activeKey === "inventory.balance" && <BalancePage />}
          {activeKey === "inventory.kardex" && <KardexPage />}
          {activeKey === "inventory.adjustments" && !adjustmentDetailId && <AdjustmentsPage onOpenDetail={setAdjustmentDetailId} />}
          {activeKey === "inventory.adjustments" && adjustmentDetailId && <AdjustmentDetailPage adjustmentId={adjustmentDetailId} onBack={() => setAdjustmentDetailId(null)} />}
          {activeKey === "analytics.dashboard" && <DashboardPage />}
          {activeKey === "analytics.reports" && <ReportsPage />}
          {activeKey === "settings.typeUse" && <TypeUsePage />}
        {activeKey === "settings.typeUnit" && <TypeUnitPage />}
        {activeKey === "settings.movementSubtype" && <MovementSubtypePage />}
        {activeKey === "settings.unitConversion" && <UnitConversionPage />}
        {activeKey === "settings.category" && <CategoryPage />}
        {activeKey === "admin.users" && <UsersPage />}
        {activeKey === "admin.usage" && <PlanUsagePage />}
        {activeKey === "settings" && <SettingsPlaceholder />}
        </div>
      </main>
    </div>
  );
});

const SettingsPlaceholder = memo(function SettingsPlaceholder() {
  const { t } = useI18n();
  return (
    <div style={{ padding: "32px 40px", color: "#888", fontFamily: "'Satoshi', sans-serif" }}>
      {t.platform.settings} — coming soon
    </div>
  );
});
